import './KSButton.scss';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import type { ReactNode, MouseEvent } from 'react';
import { Tooltip } from '../Tooltip';
import type { TooltipConfig } from '../hooks/useTooltip';

export type KSButtonVariant = 'primary' | 'secondary' | 'tertiary';

export interface KSButtonProps {
  label: ReactNode;
  icon?: ReactNode;
  /** Position of the icon relative to the label (default: 'left') */
  iconPosition?: 'left' | 'right';
  variant?: KSButtonVariant;
  pill?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  tooltip?: string | TooltipConfig;
  ariaLabel?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

const KSButton = ({
  label,
  icon,
  iconPosition = 'left',
  variant = 'secondary',
  pill = false,
  className,
  type = 'button',
  disabled = false,
  tooltip,
  ariaLabel,
  onClick,
}: KSButtonProps) => {
  const prefix = prefixClass('ksq2-button');

  const iconElement = icon && <span className={prefix('icon')}>{icon}</span>;

  const button = (
    <button
      type={type}
      className={classNames(prefix(), variant, className, {
        pill,
        'has-icon': !!icon,
        'icon-right': !!icon && iconPosition === 'right',
        disabled,
      })}
      disabled={disabled}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {iconPosition === 'left' && iconElement}
      <span className={prefix('label')}>{label}</span>
      {iconPosition === 'right' && iconElement}
    </button>
  );

  if (tooltip) {
    const tooltipConfig =
      typeof tooltip === 'string'
        ? { content: tooltip, position: 'top' as const, alignment: 'center' as const }
        : tooltip;
    return <Tooltip {...tooltipConfig}>{button}</Tooltip>;
  }

  return button;
};

export default KSButton;
